import { KetteEinbauArt } from '@hooks/states/echainConfigSlice';
import { useEchainProductStore } from '@hooks/states/useEchainProductStore';
import { useProductPageStore } from '@hooks/states/useProductPageStore';
import type { ProductAttribute, ProductAttributeValueAmountUnit } from '@libs/attribute.helper';
import { getAttributeValue } from '@libs/attribute.helper';
import { convertProductAttributeValueAmountUnit } from '@libs/units';
import { useTranslation } from 'next-i18next';
import { useLengthUnitCodeForUnitSystem } from './useLengthUnitCodeForUnitSystem';

export const useVerfahrwegWarning = () => {
  const lengthUnitCodeByLocale = useLengthUnitCodeForUnitSystem();

  const currentProduct = useProductPageStore(state => state.currentProduct);
  const kette = useEchainProductStore(state => state.kette);
  const verfahrweg = kette.Verfahrweg?.Value;
  const ketteEinbauart = useEchainProductStore(state => state.einbauArt);
  const { t } = useTranslation(['product/echainBuyBox']);

  const travelDistanceMinAttribute = getAttributeValue<ProductAttributeValueAmountUnit>(
    currentProduct.attributes?.travel_distance_supported_min_metric as ProductAttribute<ProductAttributeValueAmountUnit>
  );

  const travelDistanceMaxAttribute = getAttributeValue<ProductAttributeValueAmountUnit>(
    currentProduct.attributes?.travel_distance_supported_max_metric as ProductAttribute<ProductAttributeValueAmountUnit>
  );

  if (ketteEinbauart !== KetteEinbauArt.gleitend || !travelDistanceMinAttribute || !travelDistanceMaxAttribute) {
    return {
      showVerfahrwegWarning: false,
      verfahrwegWarningText: '',
    };
  }

  // metric values for metric unit system on the product will always be given in 'm', but verfahrweg is entered in 'mm',
  // therefore we need to convert the values to make them comparable
  const travelDistanceMin = convertProductAttributeValueAmountUnit(travelDistanceMinAttribute, lengthUnitCodeByLocale);
  const travelDistanceMax = convertProductAttributeValueAmountUnit(travelDistanceMaxAttribute, lengthUnitCodeByLocale);

  // when the input field is over or under max/min values, the input
  // Verfahrweg won't get updated, therefore we need to check
  // verfahrwegHasErrors as well when displaying the error message
  const showVerfahrwegWarning = !verfahrweg || verfahrweg < travelDistanceMin || verfahrweg > travelDistanceMax;

  const verfahrwegWarningText = (() => {
    if (verfahrweg && verfahrweg < travelDistanceMin) {
      return t('ECHAIN_PRODUCT_PAGE.TOOLTIPS.VERFAHRWEG_TOO_SMALL_WARNING', {
        ns: 'product/echainBuyBox',
        travel_distance_supported_min_metric: travelDistanceMinAttribute.amount?.toString(),
      });
    }

    return t('ECHAIN_PRODUCT_PAGE.TOOLTIPS.VERFAHRWEG_TOO_LONG_WARNING', {
      ns: 'product/echainBuyBox',
      travel_distance_supported_max_metric: travelDistanceMaxAttribute.amount?.toString(),
    });
  })();

  return {
    showVerfahrwegWarning,
    verfahrwegWarningText,
  };
};
